import React from 'react';

import { faFileInvoice, faRightFromBracket } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link, useParams } from 'react-router-dom';

import BooleanBadge from 'src/features/membership/components/BooleanBadge';
import { PATH } from 'src/features/navigation/utils/constants';
import { useGetPayStation } from 'src/features/payment/api/useGetPayStation';
import DeletePayStationFormModal from 'src/features/payment/components/forms/DeletePayStationFormModal';
import UpdatePayStationFormModal from 'src/features/payment/components/forms/UpdatePayStationFormModal';
import CashLevelsTable from 'src/features/payment/components/tables/CashLevelsTable';
import PayStationEventsTable from 'src/features/payment/components/tables/PayStationEventsTable';
import OperationsTable from 'src/features/payment/components/tables/OperationsTable';
import { paymentsMessages } from 'src/features/payment/utils/messages';
import { PayStationNominal } from 'src/features/payment/utils/types';
import DashboardGeneralBlock from 'src/features/stay/components/dashboard/DashboardGeneralBlock';
import { LOCALE } from 'src/providers/types';
import { GeneralBlock } from 'src/registry/components/blocks/GeneralBlock';
import {
    SectionBlock,
    SectionBlockHead,
    SectionBlockHeader,
    SectionBlockTitle,
    SectionBlockSubtitle,
} from 'src/registry/components/blocks/SectionBlock';
import { Button } from 'src/registry/ui/Button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from 'src/registry/ui/Tabs';
import { generalMessages } from 'src/registry/utils/messages';
import { useTitleStore } from 'src/stores/useTitleStore';
import { ICON_MAP } from 'src/utils/constants';
import { useParking } from 'src/providers/ParkingProvider';
import useHasPermissions from 'src/hooks/useHasPermissions';
import { PERMISSION } from 'src/features/auth';

const formatNominals = (nominals?: PayStationNominal[]) => {
    if (!nominals) return '-';

    return nominals.reduce((acc, nominal) => {
        if (nominal.enable) acc = `${acc}${acc && ', '}${nominal.value} ${nominal.currency}`;

        return acc;
    }, '');
};
export default function PayStationDashboard() {
    const { formatMessage } = useIntl();

    const { payStationId = '' } = useParams();

    const { setTitle, removeTitle } = useTitleStore();

    const { parkingId } = useParking();
    const { data } = useGetPayStation({ parkingId, payStationId });

    React.useEffect(() => {
        setTitle(formatMessage(paymentsMessages.payStationDashboard));

        return removeTitle;
    }, [formatMessage, removeTitle, setTitle]);

    const managePayStationsPermission = useHasPermissions([PERMISSION.MANAGE_PAY_STATIONS]);
    const accessPayStationEvents = useHasPermissions([PERMISSION.ACCESS_PAY_STATION_EVENTS]);
    const accessPayStationOperations = useHasPermissions([PERMISSION.ACCESS_PAY_STATION_OPERATIONS]);
    const accessPayStation = useHasPermissions([PERMISSION.ACCESS_PAY_STATIONS]);

    const defaultTabValue = (() => {
        if (accessPayStationEvents) return 'events';
        if (accessPayStationOperations) return 'operations';
        return 'cash-levels';
    })();

    const dashboardData = {
        section1: [
            {
                title: paymentsMessages.servicePassword,
                value: data?.settings.password,
            },
            {
                title: paymentsMessages.languages,
                value: data?.settings?.languages?.join(', '),
            },
            {
                title: paymentsMessages.defaultLanguage,
                value: data?.settings.default_language,
            },
            {
                title: paymentsMessages.currencies,
                value: data?.settings?.currencies?.join(', '),
            },
            {
                title: paymentsMessages.lostTicket,
                value: <BooleanBadge booleanField={data?.settings.lost_ticket} />,
            },
            {
                title: paymentsMessages.webId,
                value: data?.settings.web_id,
            },
            {
                title: paymentsMessages.homeQR,
                value: data?.settings.home_qr_value,
            },
            {
                title: paymentsMessages.supportPhone,
                value: data?.settings.support_phone,
            },
            {
                title: paymentsMessages.homePayTextEN,
                value: data?.settings?.home_pay_button_text?.[LOCALE.EN],
            },
            {
                title: paymentsMessages.homePayTextBG,
                value: data?.settings?.home_pay_button_text?.[LOCALE.BG],
            },
            {
                title: paymentsMessages.enablePaymentMethods,
                value: data?.settings?.enabled_payment_methods?.join(', '),
            },
            {
                title: paymentsMessages.enableFreeStay,
                value: <BooleanBadge booleanField={data?.settings.enable_free_stay} />,
            },
            {
                title: paymentsMessages.enableSettingsEnter,
                value: <BooleanBadge booleanField={data?.settings.enable_settings_enter} />,
            },
            {
                title: paymentsMessages.enableSubscription,
                value: <BooleanBadge booleanField={data?.settings.enable_subscriptions} />,
            },
            {
                title: paymentsMessages.coinPayoutNominals,
                value: formatNominals(data?.settings?.coin_selector?.payout_nominals),
            },
            {
                title: paymentsMessages.coinAcceptedNominals,
                value: formatNominals(data?.settings?.coin_selector?.accepted_nominals),
            },
            {
                title: paymentsMessages.banknotePayoutNominals,
                value: formatNominals(data?.settings?.banknote_validator?.payout_nominals),
            },
            {
                title: paymentsMessages.banknoteAcceptedNominals,
                value: formatNominals(data?.settings?.banknote_validator?.accepted_nominals),
            },
        ],
    };

    return (
        <>
            <Button asChild className="-mt-6 place-self-start p-0" variant="link">
                <Link to={`/${PATH.PAYMENTS}/pay-station`}>
                    <FontAwesomeIcon icon={ICON_MAP.BACK} className="opacity-50" />
                    {formatMessage(paymentsMessages.backToAllPayStations)}
                </Link>
            </Button>

            <div className="flex flex-col gap-4 sm:gap-6 md:flex-row">
                <SectionBlock>
                    <SectionBlockHeader className="flex min-h-9 flex-row justify-between">
                        <SectionBlockHead>
                            <SectionBlockTitle>{data?.name}</SectionBlockTitle>
                            <SectionBlockSubtitle>{data?.code}</SectionBlockSubtitle>
                        </SectionBlockHead>
                        {managePayStationsPermission && data && (
                            <div className="flex flex-row gap-2">
                                <UpdatePayStationFormModal selectedPayStation={data}>
                                    <Button size="mdIcon" variant="outline">
                                        <div className="block md:hidden">
                                            <FontAwesomeIcon icon={faFileInvoice} />
                                        </div>
                                        <span className="hidden md:block">{formatMessage(generalMessages.edit)}</span>
                                    </Button>
                                </UpdatePayStationFormModal>
                                <DeletePayStationFormModal selectedPayStation={data}>
                                    <Button size="mdIcon" variant="destructiveLight">
                                        <div className="block md:hidden">
                                            <FontAwesomeIcon className="block md:hidden" icon={faRightFromBracket} />
                                        </div>
                                        <span className="hidden md:block">{formatMessage(generalMessages.delete)}</span>
                                    </Button>
                                </DeletePayStationFormModal>
                            </div>
                        )}
                    </SectionBlockHeader>
                    <GeneralBlock asChild className="flex-1 2xl:flex-2">
                        <DashboardGeneralBlock dashboardData={dashboardData} indexForLastSeparator={0} />
                    </GeneralBlock>
                </SectionBlock>
            </div>

            <Tabs defaultValue={defaultTabValue}>
                <TabsList>
                    {accessPayStationEvents && (
                        <TabsTrigger value="events">
                            <FormattedMessage {...paymentsMessages.eventLogs} />
                        </TabsTrigger>
                    )}
                    {accessPayStationOperations && (
                        <TabsTrigger value="operations">
                            <FormattedMessage {...paymentsMessages.operations} />
                        </TabsTrigger>
                    )}
                    {accessPayStation && (
                        <TabsTrigger value="cash-levels">
                            <FormattedMessage {...paymentsMessages.cashLevels} />
                        </TabsTrigger>
                    )}
                </TabsList>

                {accessPayStationEvents && (
                    <TabsContent value="events">
                        <PayStationEventsTable />
                    </TabsContent>
                )}
                {accessPayStationOperations && (
                    <TabsContent value="operations">
                        <OperationsTable />
                    </TabsContent>
                )}
                {accessPayStation && (
                    <TabsContent value="cash-levels">
                        <CashLevelsTable />
                    </TabsContent>
                )}
            </Tabs>
        </>
    );
}
